import { Injectable, Type } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { PanelModel } from '@syncfusion/ej2-layouts';
import { CreateDashboardDTO, DashboardClient, DashboardDTO, DashboardPanelDTO, IDashboardDTO, Role, UpdateDashboardDTO, UpdateDashboardPanelsDTO } from '../../../base/models/models.swagger.generated';
import { WidgetType } from './widgets/shared/widget-type.enum';
import { AuthService } from 'src/app/base/services/auth.service';
import { LogoWidgetComponent } from './widgets/other-widgets/logo-widget/logo-widget.component';
import { ToastService } from 'src/app/base/services/toast.service';
import { ErrorType } from 'src/app/core/routing/error-types';
import { ClientLogoWidgetComponent } from './widgets/other-widgets/client-logo-widget/client-logo-widget.component';
import { DeviceStateWidgetComponent } from './widgets/device-widgets/device-state-widget/device-state-widget.component';
import { DeviceControlWidgetComponent } from './widgets/device-widgets/device-control-widget/device-control-widget.component';
import { DeviceOverviewWidgetComponent } from './widgets/device-widgets/device-overview-widget/device-overview-widget.component';

export interface ExtendedPanelModel extends PanelModel {
  widgetType: number;
  widgetParameters: string;
}

export interface IExtendedDashboardDTO extends IDashboardDTO {
  extendedDashboardPanels: ExtendedPanelModel[];
}

export interface WidgetTypeGridModel {
  id: number;
  widgetType: string;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  private dashboardsSubject = new BehaviorSubject<IExtendedDashboardDTO[]>([]);
  public dashboards$ = this.dashboardsSubject.asObservable();

  constructor(private dashboardClient: DashboardClient,
              private authService: AuthService,
              private toastService: ToastService) {
  }

  resolve(): Observable<DashboardDTO[]> {
    return this.dashboardClient.getDashboards().pipe(
      mergeMap((dashboards: DashboardDTO[]) => {
        if (dashboards && dashboards.length > 0) {
          const sortedDashboards = dashboards.map(d => this.mapToExtendedDashboardDTO(d)).sort((a, b) => a.tabGroupIndex - b.tabGroupIndex);
          this.dashboardsSubject.next(sortedDashboards);
          return of(dashboards);
        } else {
          return this.addDashboard(new CreateDashboardDTO({
            name: 'Dashboard',
            imageData: null,
            tabGroupIndex: 0
          })).pipe(
            mergeMap((dashboard: DashboardDTO) => {
              this.dashboardsSubject.next([this.mapToExtendedDashboardDTO(dashboard)]);
              return of([dashboard]);
            })
          );
        }
      })
    );
  }

  public updateDashboardPanels(updateDashboardPanelsData: UpdateDashboardPanelsDTO): Observable<DashboardDTO> {
    return this.dashboardClient.updateDashboardPanels(updateDashboardPanelsData).pipe(
      tap((dashboard: DashboardDTO) => {
        this.dashboardsSubject.next(this.dashboardsSubject.value.map(d => d.id === dashboard.id ? this.mapToExtendedDashboardDTO(dashboard) : d));
      })
    );
  }

  public addDashboard(dashboard: CreateDashboardDTO): Observable<DashboardDTO> {
    return this.dashboardClient.addDashboard(dashboard).pipe(
      mergeMap((dashboard: DashboardDTO) => {
        this.dashboardsSubject.next(this.dashboardsSubject.value.concat([this.mapToExtendedDashboardDTO(dashboard)]));
        //Add a default widget to the dashboard
        this.updateDashboardPanels(new UpdateDashboardPanelsDTO({
          dashboardId: dashboard.id,
          addedPanels: [new DashboardPanelDTO({
            id: 0,
            sizeX: 5,
            sizeY: 4,
            row: 0,
            col: 3,
            widgetType: WidgetType.LogoWidget.toString(),
          })]
        })).subscribe();
        return of(dashboard);
      }),
      catchError((error: any) => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  public updateDashboard(dashboard: UpdateDashboardDTO): Observable<DashboardDTO> {
    return this.dashboardClient.updateDashboard(dashboard).pipe(
      tap((updatedDashboard: DashboardDTO) => {
        const currentDashboards = this.dashboardsSubject.getValue();
        currentDashboards.forEach((d, index) => {
          if (d.id === updatedDashboard.id) {
            currentDashboards[index] = this.mapToExtendedDashboardDTO(updatedDashboard);
          }
        });
        this.dashboardsSubject.next([...currentDashboards]);
      }),
      catchError((error: any) => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  public deleteDashboard(dashboardId: number): Observable<void> {
    return this.dashboardClient.deleteDashboard(dashboardId).pipe(
      tap(() => {
        this.dashboardsSubject.next(this.dashboardsSubject.value.filter(d => d.id !== dashboardId));
      }),
      catchError((error: any) => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  //Access control for widgets
  public canUserAccesWidget(widgetType: WidgetType): boolean {
    // IMPORTANT: follow widgetType enum order for better maintenance
    switch (widgetType) {
      case WidgetType.LogoWidget:
        return true;
      case WidgetType.ClientLogoWidget:
        return false; // TODO: widget is not implemented yet
      case WidgetType.DeviceStateWidget:
        // TODO: once device state widget is tested for clientadmins and companyadmins enabled it for them as well
        return this.authService.isUserAdmin();
      case WidgetType.DeviceControlWidget:
        // TODO: once device state widget is tested for clientadmins and companyadmins enabled it for them as well
        return this.authService.isUserAdmin();
      case WidgetType.DeviceOverviewWidget:
        //return this.authService.isUserAdminOrClientAdmin();
        return false; // TODO: widget is not implemented yet
    }
  }

  public getListOfWidgetsUserCanAccess(): WidgetTypeGridModel[] {
    const widgets: WidgetTypeGridModel[] = [];
    for (const widgetType in WidgetType) {
      if (!isNaN(widgetType as any)) {
        continue;
      }
      const widgetTypeNumber = Number(WidgetType[widgetType]);
      if (this.canUserAccesWidget(widgetTypeNumber)) {
        widgets.push({
          id: widgetTypeNumber,
          widgetType:  'i18n.domain.home.dashboard.shared.widget-types.' + widgetType
        });
      }
    }
    return widgets
  }
  //End of access control for widgets

  public getWidgetClass(widgetType: WidgetType): Type<any> {
    // IMPORTANT: follow widgetType enum order for better overview
    switch(widgetType) {
      case WidgetType.LogoWidget: {
        return LogoWidgetComponent;
      }
      case WidgetType.ClientLogoWidget: {
        return ClientLogoWidgetComponent;
      }
      case WidgetType.DeviceStateWidget: {
        return DeviceStateWidgetComponent;
      }
      case WidgetType.DeviceControlWidget: {
        return DeviceControlWidgetComponent;
      }
      case WidgetType.DeviceOverviewWidget: {
        return DeviceOverviewWidgetComponent;
      }
    }
  }

  public isPersonalMode(widgetType: WidgetType): boolean {
    switch(widgetType) {
      //Implement once personal widgets are needed
      // case WidgetType.TurnoverPersonalWidget:
      // case WidgetType.DBMarginPersonalWidget:
      // case WidgetType.NextwayPersonalTasksWidget:
      // case WidgetType.ProjectScoringPersonalWidget:
      // case WidgetType.WorkProcessScoringPersonalWidget:
      // case WidgetType.NextwayPersonalTodosWidget: {
      //   return true;
      // }
      default:
        return false;
    }
  }

  public doesPanelCollide(dashboardId: number, panelToCheck: ExtendedPanelModel): boolean {
    let collisionFound = false;
    this.dashboardsSubject.value?.find(d => d.id === dashboardId)?.dashboardPanels?.forEach(panel => {
      collisionFound = collisionFound || this.isCoveredByPanel(panel.col, panel.row, panelToCheck);
      collisionFound = collisionFound || this.isCoveredByPanel(panel.col + panel.sizeX - 1, panel.row, panelToCheck);
      collisionFound = collisionFound || this.isCoveredByPanel(panel.col, panel.row + panel.sizeY - 1, panelToCheck);
      collisionFound = collisionFound || this.isCoveredByPanel(panel.col + panel.sizeX - 1, panel.row + panel.sizeY - 1, panelToCheck);
    });
    return collisionFound;
  }

  private isCoveredByPanel(col: number, row: number, panel: ExtendedPanelModel): boolean {
    if (col >= panel.col && col < panel.col + panel.sizeX && row >= panel.row && row < panel.row + panel.sizeY) {
      return true;
    }
    return false;
  }

  private mapToExtendedDashboardDTO(dashboard: DashboardDTO): IExtendedDashboardDTO {
    return {
      ...dashboard,
      extendedDashboardPanels: this.createExtendedPanelsForDashboard(dashboard)
    };
  }

  private createExtendedPanelsForDashboard(dashboard: DashboardDTO): ExtendedPanelModel[] {
    let panelModels: ExtendedPanelModel[] = [];
    dashboard.dashboardPanels?.forEach((panel) => {
      panelModels.push({
        id: panel.id.toString(),
        sizeX: panel.sizeX,
        sizeY: panel.sizeY,
        row: panel.row,
        col: panel.col,
        widgetType: +panel.widgetType,
        widgetParameters: panel.widgetParameters
      } as ExtendedPanelModel);
    });
    return panelModels;
  }
}
